import {
  ClientApp,
  ColumnModel,
  LinkPairKind,
  link_pair_kind,
  PhysicalKind,
  TableLinkJS,
} from "target_wasm";
import { compareColumnsById } from "../tableDetails/ColumnList/buildColumnItems";

export interface LinkedPartner {
  id: string;
  name: string;
}

export function getLinkedColumnPartners(
  clientApp: ClientApp | null,
  thisTableId: string | null,
  otherTableId: string | null,
): Map<string, LinkedPartner[]> {
  const out = new Map<string, LinkedPartner[]>();
  if (!clientApp || !thisTableId || !otherTableId) return out;

  const add = (columnId: string, partner: LinkedPartner) => {
    const partners = out.get(columnId) ?? [];
    partners.push(partner);
    out.set(columnId, partners);
  };

  try {
    for (const link of clientApp.get_links_for_table(thisTableId)) {
      const touchesOther =
        link.first_table_id_js === otherTableId ||
        link.second_table_id_js === otherTableId;
      if (!touchesOther) continue;
      if (link.first_table_id_js === thisTableId) {
        link.first_column_ids_js.forEach((id, i) =>
          add(id, {
            id: link.second_column_ids_js[i] ?? "",
            name: link.second_column_names_js[i] ?? "",
          }),
        );
      }
      if (link.second_table_id_js === thisTableId) {
        link.second_column_ids_js.forEach((id, i) =>
          add(id, {
            id: link.first_column_ids_js[i] ?? "",
            name: link.first_column_names_js[i] ?? "",
          }),
        );
      }
    }
  } catch (error) {
    console.error("Error getting linked columns between tables:", error);
  }
  return out;
}

/** The link's column ids on `tableId`'s side, both sides for a self-link. */
const columnsOn = (link: TableLinkJS, tableId: string): string[] => [
  ...(link.first_table_id_js === tableId ? link.first_column_ids_js : []),
  ...(link.second_table_id_js === tableId ? link.second_column_ids_js : []),
];

/** Every column of `tableId` a link uses. Throws what the WASM read throws. */
export function readLinkedColumnIds(
  clientApp: ClientApp,
  tableId: string,
): Set<string> {
  return new Set(
    clientApp
      .get_links_for_table(tableId)
      .flatMap((l) => columnsOn(l, tableId)),
  );
}

/** An Attachment column carries files, not values: never a link end. */
export function canDataTypeBeLinked(dataType: PhysicalKind): boolean {
  return (
    dataType !== PhysicalKind.ND &&
    dataType !== PhysicalKind.F64 &&
    dataType !== PhysicalKind.F32 &&
    dataType !== PhysicalKind.Attachment
  );
}

export function isColumnNotAllowed(
  column: ColumnModel,
  secondColumnDataType: PhysicalKind | null,
  stagedPairCount = 0,
) {
  if (!canDataTypeBeLinked(column.data_type)) return true;
  // Membership (String ∈ list_string) is single-pair only, so once a pair
  // is staged the composite can only grow with equality pairs — list
  // columns leave the picker. The wasm resolver is the same rule the
  // server's 400 runs; the arity gate here is affordance, the server the
  // authority.
  if (stagedPairCount > 0 && column.data_type === PhysicalKind.ListString) {
    return true;
  }
  if (secondColumnDataType === null) return false;
  const kind = link_pair_kind(column.data_type, secondColumnDataType);
  if (kind === LinkPairKind.Incompatible) return true;
  return kind === LinkPairKind.Membership && stagedPairCount > 0;
}

/**
 * The resolved kind of an existing link, from the client's schema state.
 * `null` when either side's column type is unreachable (fail-soft — treat
 * as a plain link).
 */
export function getLinkPairKind(
  clientApp: ClientApp | null,
  link: TableLinkJS,
): LinkPairKind | null {
  // Membership is single-pair by definition, so a composite's first pair
  // cannot decide its kind.
  if (link.first_column_ids_js.length > 1) return LinkPairKind.Equality;
  const types = linkColumnTypes(clientApp, link);
  if (!types) return null;
  return link_pair_kind(types[0], types[1]);
}

/**
 * Which endpoint holds the list column of a membership link, `null` for
 * every other link. Mirrors `LinkKind::Membership { list_side }`, which the
 * payload-free `LinkPairKind` drops.
 */
export function getLinkListSide(
  clientApp: ClientApp | null,
  link: TableLinkJS,
): "a" | "b" | null {
  if (getLinkPairKind(clientApp, link) !== LinkPairKind.Membership) {
    return null;
  }
  const types = linkColumnTypes(clientApp, link);
  if (!types) return null;
  return types[0] === PhysicalKind.ListString ? "a" : "b";
}

function linkColumnTypes(
  clientApp: ClientApp | null,
  link: TableLinkJS,
): [PhysicalKind, PhysicalKind] | null {
  if (!clientApp) return null;
  const colType = (tableId: string, columnId?: string) => {
    if (!columnId) return null;
    try {
      // Keyed by `tab-` id; a `modit-` reader throws on one.
      const [col] = clientApp.column_models(tableId, [
        columnId,
      ]) as ColumnModel[];
      return col?.data_type ?? null;
    } catch {
      return null;
    }
  };
  const first = colType(link.first_table_id_js, link.first_column_ids_js?.[0]);
  const second = colType(
    link.second_table_id_js,
    link.second_column_ids_js?.[0],
  );
  if (first == null || second == null) return null;
  return [first, second];
}

export function filterAndSortColumns(
  columns: ColumnModel[],
  isSameTable: boolean,
  secondColumnId: string | null,
  secondColumnDataType: PhysicalKind | null,
  linkedColumns: string[],
  stagedPairCount = 0,
) {
  const filtered = columns.filter((column) => {
    const isSameSelectedColumn = isSameTable && secondColumnId === column.id;
    return (
      !isColumnNotAllowed(column, secondColumnDataType, stagedPairCount) &&
      !isSameSelectedColumn
    );
  });
  filtered.sort(compareColumnsById);

  return filtered.sort((a, b) => {
    const aHasLink = linkedColumns.includes(a.id);
    const bHasLink = linkedColumns.includes(b.id);
    return aHasLink === bHasLink ? 0 : aHasLink ? 1 : -1;
  });
}

/** "T1.a ↔ T2.x", or "T1.(a, b) ↔ T2.(x, y)" for a composite key. */
export function linkLabel(link: TableLinkJS): string {
  const side = (table: string, names: string[]) =>
    names.length > 1
      ? `${table}.(${names.join(", ")})`
      : `${table}.${names[0] ?? ""}`;
  return `${side(link.first_table_name_js, link.first_column_names_js)} ↔ ${side(
    link.second_table_name_js,
    link.second_column_names_js,
  )}`;
}
